import { useEffect, useRef, useState } from "react";
import * as tus from "tus-js-client";
import { X, UploadCloud, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { TID } from "@/constants/testIds";

// Iter 43 — Universal upload. Videos → Bunny Stream (TUS). Photos + PDFs →
// Bunny Storage via `/api/assets/upload-file` (multipart). Kind is inferred
// from MIME so users just drop any file and the modal routes it correctly.
const PHOTO_MIME_RE = /^image\/(jpeg|jpg|png|webp|gif|heic|heif)$/i;
const PDF_MIME_RE = /^application\/pdf$/i;
const UPLOAD_CONCURRENCY = 5;
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];
const RETRYABLE_STATUS = new Set([0, 429, 502, 503, 504]);

function fileKind(file) {
  const m = (file.type || "").toLowerCase();
  if (m.startsWith("video/")) return "video";
  if (PHOTO_MIME_RE.test(m)) return "photo";
  if (PDF_MIME_RE.test(m)) return "pdf";
  return "unsupported";
}

export default function UploadModal({ folderId, onClose, onUploaded, replaceAssetId, replaceAssetTitle }) {
  const isReplaceMode = !!replaceAssetId;
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState([]); // [{file, progress, status, assetId, error, kind}]
  const [carryFeedback, setCarryFeedback] = useState(false);
  const dragRef = useRef(null);

  const addFiles = (newFiles) => {
    // Replace mode is video-only (Bunny Stream); non-video files are silently skipped there.
    const accepted = Array.from(newFiles).filter((f) => {
      const k = fileKind(f);
      if (isReplaceMode) return k === "video";
      return k !== "unsupported";
    });
    setFiles((prev) => isReplaceMode ? accepted.slice(0, 1) : [...prev, ...accepted]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragRef.current?.classList.remove("border-[var(--brand-accent)]");
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const patchItem = (idx, patch) => setItems((prev) => {
    const next = [...prev];
    next[idx] = { ...next[idx], ...patch };
    return next;
  });

  // Single multipart attempt → resolves {ok, status, data|msg, retryAfterMs}; never rejects.
  const sendNonVideoOnce = (idx, file) => new Promise((resolve) => {
    const fd = new FormData();
    fd.append("folder_id", folderId);
    fd.append("title", file.name.replace(/\.[^/.]+$/, ""));
    fd.append("file", file);
    const xhr = new XMLHttpRequest();
    const token = localStorage.getItem("wd_token");
    xhr.open("POST", `${API}/assets/upload-file`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) patchItem(idx, { progress: Math.round((e.loaded / e.total) * 100) });
    };
    xhr.onload = () => {
      if (xhr.status === 200) return resolve({ ok: true, data: JSON.parse(xhr.responseText) });
      let msg = "Upload failed";
      try { msg = JSON.parse(xhr.responseText).detail || msg; } catch {}
      const ra = Number(xhr.getResponseHeader("Retry-After"));
      resolve({ ok: false, status: xhr.status, msg, retryAfterMs: ra > 0 ? ra * 1000 : 0 });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, msg: "Network error" });
    xhr.send(fd);
  });

  // Photos + PDFs → Bunny Storage via multipart, with exponential backoff on
  // network errors and 429/502/503/504 (mirrors the TUS retryDelays on videos).
  const uploadNonVideoFile = async (idx) => {
    const file = files[idx];
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { file, progress: 0, status: "uploading", kind: fileKind(file) };
      return next;
    });
    for (let attempt = 0; ; attempt++) {
      const res = await sendNonVideoOnce(idx, file);
      if (res.ok) {
        patchItem(idx, { status: "processing", progress: 100, assetId: res.data.id, error: undefined });
        onUploaded?.();
        return;
      }
      if (!RETRYABLE_STATUS.has(res.status) || attempt >= RETRY_DELAYS_MS.length) {
        patchItem(idx, { status: "error", error: res.msg });
        return;
      }
      const delay = Math.max(RETRY_DELAYS_MS[attempt] + Math.random() * 500, res.retryAfterMs || 0);
      patchItem(idx, { status: "retrying", progress: 0, error: undefined, attempt: attempt + 1 });
      await new Promise((r) => setTimeout(r, delay));
      patchItem(idx, { status: "uploading" });
    }
  };

  const uploadFile = (idx) => new Promise((done) => {
    const file = files[idx];
    const kind = fileKind(file);
    // Iter 43 — Route non-videos through Bunny Storage (multipart).
    if (kind === "photo" || kind === "pdf") {
      uploadNonVideoFile(idx).then(done);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { file, progress: 0, status: "initializing", kind };
      return next;
    });

    const initUrl = isReplaceMode ? `/assets/${replaceAssetId}/replace-init` : "/assets/init-upload";
    const initBody = isReplaceMode
      ? { title: file.name.replace(/\.[^/.]+$/, ""), folder_id: "" }
      : { title: file.name.replace(/\.[^/.]+$/, ""), folder_id: folderId };

    api.post(initUrl, initBody)
      .then(({ data }) => {
        setItems((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], status: "uploading", assetId: data.asset_id };
          return next;
        });

        const upload = new tus.Upload(file, {
          endpoint: data.tus_endpoint,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          headers: {
            AuthorizationSignature: data.signature,
            AuthorizationExpire: String(data.expire),
            VideoId: data.video_id,
            LibraryId: data.library_id,
          },
          metadata: {
            filetype: file.type,
            title: data.title,
          },
          onError: (error) => {
            setItems((prev) => {
              const next = [...prev];
              next[idx] = { ...next[idx], status: "error", error: error.message || "Upload failed" };
              return next;
            });
            done();
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const progress = Math.round((bytesUploaded / bytesTotal) * 100);
            setItems((prev) => {
              const next = [...prev];
              next[idx] = { ...next[idx], progress };
              return next;
            });
          },
          onSuccess: async () => {
            try {
              if (isReplaceMode) {
                await api.post(`/assets/${replaceAssetId}/finalize-replace`, null, {
                  params: { carry_feedback_forward: carryFeedback },
                });
              } else {
                await api.post(`/assets/${data.asset_id}/mark-uploaded`);
              }
            } catch (e) {
              // non-fatal; webhook will still update for normal uploads
            }
            setItems((prev) => {
              const next = [...prev];
              next[idx] = { ...next[idx], status: "processing", progress: 100 };
              return next;
            });
            onUploaded?.();
            done();
          },
        });
        upload.start();
      })
      .catch((err) => {
        setItems((prev) => {
          const next = [...prev];
          next[idx] = { file, status: "error", error: err.response?.data?.detail || "Init failed" };
          return next;
        });
        done();
      });
  });

  // Bounded worker pool: UPLOAD_CONCURRENCY workers each pull the next queued file.
  const startedRef = useRef(new Set());
  const startAll = () => {
    const queue = files.map((_, idx) => idx).filter((idx) => !items[idx] && !startedRef.current.has(idx));
    queue.forEach((idx) => startedRef.current.add(idx));
    const worker = async () => {
      while (queue.length) await uploadFile(queue.shift());
    };
    for (let i = 0; i < Math.min(UPLOAD_CONCURRENCY, queue.length); i++) worker();
  };

  const allDone = files.length > 0 && items.length === files.length && items.every((i) => i?.status === "processing" || i?.status === "error");

  useEffect(() => {
    if (allDone) onUploaded?.();
  }, [allDone, onUploaded]);

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid={TID.uploadDialog}
        className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/60">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">
              {isReplaceMode ? "Replace video" : "Upload assets"}
            </h2>
            {!isReplaceMode && (
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Videos, photos (jpg / png / webp / heic), and PDFs — all in one drop.
              </div>
            )}
            {isReplaceMode && (
              <div className="text-[11px] text-zinc-500 mt-0.5 truncate max-w-md">
                Replacing: <span className="text-zinc-300">{replaceAssetTitle}</span>
              </div>
            )}
          </div>
          <button data-testid={TID.uploadClose} onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {isReplaceMode && (
            <label className="flex items-start gap-2 cursor-pointer bg-black/30 border border-zinc-800/60 rounded-lg p-3">
              <input
                type="checkbox"
                checked={carryFeedback}
                onChange={(e) => setCarryFeedback(e.target.checked)}
                className="mt-0.5 accent-[var(--brand-accent)]"
              />
              <div>
                <div className="text-sm text-zinc-200">Carry feedback forward</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Keep existing comments + approvals attached to the new version. Use this for <span className="text-zinc-300">minor revisions</span> like color or graphic fixes. Leave unchecked when timing has changed — existing feedback will be archived under the previous version either way.
                </div>
              </div>
            </label>
          )}

          <div
            ref={dragRef}
            onDragOver={(e) => { e.preventDefault(); dragRef.current?.classList.add("border-[var(--brand-accent)]"); }}
            onDragLeave={() => dragRef.current?.classList.remove("border-[var(--brand-accent)]")}
            onDrop={onDrop}
            className="border-2 border-dashed border-zinc-800 rounded-lg p-8 text-center transition-colors"
          >
            <UploadCloud className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
            <p className="text-sm text-zinc-400">{isReplaceMode ? "Drop the new version of this video here" : "Drag & drop videos, photos, or PDFs here"}</p>
            <p className="text-xs text-zinc-600 mt-1">or</p>
            <label className="inline-block mt-2 cursor-pointer">
              <input
                data-testid={TID.uploadFileInput}
                type="file"
                multiple={!isReplaceMode}
                accept={isReplaceMode ? "video/*" : "video/*,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf"}
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
              <span className="text-sm text-[var(--brand-accent)] hover:underline">browse from your device</span>
            </label>
          </div>

          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, idx) => {
                const it = items[idx];
                return (
                  <div key={idx} data-testid={TID.uploadProgress(idx)} className="bg-black/30 border border-zinc-800/60 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-200 truncate">{file.name}</div>
                        <div className="text-[10px] text-zinc-600 uppercase tracking-wider">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                      </div>
                      <div className="ml-3 shrink-0">
                        {!it && <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Queued</span>}
                        {it?.status === "initializing" && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />}
                        {it?.status === "retrying" && <span className="text-[10px] text-amber-400 uppercase tracking-wider" data-testid={`upload-retrying-${idx}`}>Retrying ({it.attempt})</span>}
                        {it?.status === "uploading" && <span className="text-[10px] text-[var(--brand-accent)] uppercase tracking-wider">{it.progress}%</span>}
                        {it?.status === "processing" && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                        {it?.status === "error" && <AlertCircle className="w-4 h-4 text-red-400" />}
                      </div>
                    </div>
                    {(it?.status === "uploading" || it?.status === "processing") && (
                      <div className="h-1 bg-zinc-900 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[var(--brand-accent)] transition-all duration-300"
                          style={{ width: `${it?.progress || 0}%` }}
                        />
                      </div>
                    )}
                    {it?.error && <div className="text-xs text-red-400 mt-1">{it.error}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {files.length > 0 && (
          <div className="px-6 py-4 border-t border-zinc-800/60 flex justify-end gap-2">
            <button
              onClick={() => { setFiles([]); setItems([]); startedRef.current = new Set(); }}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200"
            >
              Clear
            </button>
            <button
              data-testid={TID.uploadStartBtn}
              onClick={startAll}
              disabled={files.every((_, idx) => items[idx] || startedRef.current.has(idx))}
              className="px-4 py-2 bg-[var(--brand-accent)] text-black font-semibold rounded-md text-sm hover:bg-[var(--brand-accent)]/90 transition disabled:opacity-60"
            >
              Start upload ({files.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
